// src/components/CSAT/CsatModal.tsx
import {
  FeedbackCsat
} from "@grupoboticario/flora-team-react-feedback-csat";

// src/components/CSAT/generic.helpers.ts
var getScoreTitle = (featureOrModuleName, scoreTitleOverride) => {
  if (scoreTitleOverride) {
    return scoreTitleOverride;
  }
  return `Como voc\xEA avaliaria sua experi\xEAncia em ${featureOrModuleName}?`;
};
var buildScoreObject = (featureOrModuleName, type, feedback) => {
  if (feedback) {
    return feedback;
  }
  if (type === "done") {
    return {
      title: "Agradecemos sua resposta",
      description: `Assim voc\xEA nos ajuda a tornar a ${featureOrModuleName} cada vez melhor.`
    };
  }
  if (type === "positive") {
    return {
      title: "Do que voc\xEA gostou?",
      description: `Se quiser, deixe um coment\xE1rio sobre sua experi\xEAncia de ${featureOrModuleName}.`
    };
  }
  return {
    title: "O que pode melhorar?",
    description: `Por favor, conte o que poderia melhorar na experi\xEAncia de ${featureOrModuleName}.`
  };
};
var isValidDateString = (dateString) => {
  if (!dateString) {
    return false;
  }
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};
var shouldDisplayFeedbackModal = (lastSubmission, daysToWaitUntilNextFeedback) => {
  if (!isValidDateString(lastSubmission)) {
    return true;
  }
  if (daysToWaitUntilNextFeedback <= 0) {
    return true;
  }
  const lastSubmissionDate = new Date(lastSubmission);
  const currentDate = /* @__PURE__ */ new Date();
  const timeDiff = Math.abs(
    currentDate.getTime() - lastSubmissionDate.getTime()
  );
  const diffDays = Math.ceil(timeDiff / (1e3 * 3600 * 24));
  return diffDays >= daysToWaitUntilNextFeedback;
};
var shouldWaitUntilAfterDateToDisplay = (displayAfterDate) => {
  if (!displayAfterDate) {
    return true;
  }
  const displayDate = new Date(displayAfterDate);
  if (isNaN(displayDate.getTime())) {
    return false;
  }
  const currentDate = /* @__PURE__ */ new Date();
  if (currentDate.getTime() < displayDate.getTime()) {
    return false;
  }
  return true;
};

// src/components/CSAT/submission.helpers.ts
var saveFeedback = async (submission, apiUrl) => {
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(submission)
    });
    if (!response.status || response.status !== 201) {
      return false;
    }
    return true;
  } catch (err) {
    console.log(err);
    return false;
  }
};
var getLastSubmission = async (personId, entity, owner, apiUrl) => {
  try {
    const params = `?personId=${personId}&entity=${entity}&owner=${owner}`;
    const response = await fetch(`${apiUrl}${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });
    if (!response.status || response.status !== 200) {
      return null;
    }
    const responseData = await response.json();
    const date = responseData?.date;
    if (!date) {
      return null;
    }
    return date;
  } catch (err) {
    console.log(err);
    return null;
  }
};

// src/components/CSAT/CsatModal.tsx
import { useCallback, useEffect, useState } from "react";

// src/components/atoms/FloraContainer.tsx
import { FloraProvider } from "@grupoboticario/flora-react";
import { Fragment, jsx } from "react/jsx-runtime";
var FloraContainer = ({ children }) => {
  return /* @__PURE__ */ jsx(FloraProvider, { children: /* @__PURE__ */ jsx(Fragment, { children }) });
};

// src/components/CSAT/CsatModal.tsx
import { Toaster } from "@grupoboticario/flora-react";
import { jsx as jsx2 } from "react/jsx-runtime";
var CsatModal = ({
  featureOrModuleName,
  scoreTitleOverride,
  negativeScore,
  positiveScore,
  commentPlaceholder,
  sendButtonText,
  feedbackDone,
  owner,
  personId,
  apiUrl,
  daysToWaitUntilNextFeedback = 30,
  isEnabled = true,
  displayAfterDate = void 0
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, []);
  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onSubmit = useCallback(
    async (params) => {
      const submission = {
        comment: params.comment,
        score: params.score,
        scoreDescription: params.scoreLabel,
        owner: owner ?? "Conecta",
        entity: featureOrModuleName,
        personId
      };
      const savedFeedback = await saveFeedback(submission, apiUrl);
      if (savedFeedback) {
        return;
      }
      Toaster.notify({
        kind: "error",
        description: "Erro ao salvar feedback. Tente novamente mais tarde.",
        origin: "right-top"
      });
    },
    [featureOrModuleName, personId]
  );
  const fetchLastSubmissionAndShowModalIfNeeded = useCallback(
    async (person, entity, owner2) => {
      const lastSubmissionDateString = await getLastSubmission(
        person,
        entity,
        owner2,
        apiUrl
      );
      if (!lastSubmissionDateString) {
        if (!shouldWaitUntilAfterDateToDisplay(displayAfterDate)) {
          return;
        }
        onOpen();
        return;
      }
      const shouldDisplay = shouldDisplayFeedbackModal(
        lastSubmissionDateString,
        daysToWaitUntilNextFeedback
      );
      if (!shouldDisplay) {
        return;
      }
      onOpen();
    },
    []
  );
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    fetchLastSubmissionAndShowModalIfNeeded(
      personId,
      featureOrModuleName,
      owner ?? "Conecta"
    );
  }, []);
  return /* @__PURE__ */ jsx2(FloraContainer, { children: /* @__PURE__ */ jsx2(
    FeedbackCsat,
    {
      open: isOpen,
      texts: {
        score: {
          title: getScoreTitle(featureOrModuleName, scoreTitleOverride)
        },
        comment: {
          negativeScore: buildScoreObject(
            featureOrModuleName,
            "negative",
            negativeScore
          ),
          positiveScore: buildScoreObject(
            featureOrModuleName,
            "positive",
            positiveScore
          ),
          placeholder: commentPlaceholder ?? "Digite sua resposta aqui:",
          button: sendButtonText ?? "Enviar"
        },
        done: buildScoreObject(featureOrModuleName, "done", feedbackDone)
      },
      onOpen,
      onClose,
      onSubmit
    }
  ) });
};
export {
  CsatModal
};
